import type {
  CustomTitleBindingValue,
  CustomTitleDirective,
  CustomTitleDirectiveBinding
} from "./types";

// 用于存储当前激活的提示元素
let currentTooltipElement: HTMLDivElement | null = null;
let currentHideTimer: ReturnType<typeof setTimeout> | null = null; // 用于隐藏提示的定时器，防止快速切换闪烁

/**
 * 创建并初始化提示框元素
 * @param content 要显示的文本内容
 * @returns 创建的提示框HTML元素
 */
function createTooltipElement(content: string | CustomTitleBindingValue): HTMLDivElement {
  const tooltip = document.createElement("div");
  tooltip.className = "custom-tooltip";

  const textContent = typeof content === "string" ? content : content.content;
  tooltip.innerText = textContent; // 默认使用innerText防止XSS

  tooltip.style.cssText = `
    position: fixed;
    z-index: 999;
    padding: 0  10px 2px 10px;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    border-radius: 12px;
    font-size: 12px;
    min-width: 50px;
    height: 26px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    box-sizing: border-box; /* 确保padding不影响宽度计算 */
  `;
  document.body.appendChild(tooltip);
  currentTooltipElement = tooltip;
  return tooltip;
}

/**
 * 移除当前显示的提示框
 */
function removeTooltipElement(): void {
  if (currentHideTimer) {
    clearTimeout(currentHideTimer);
    currentHideTimer = null;
  }
  if (currentTooltipElement && currentTooltipElement.parentNode) {
    currentTooltipElement.parentNode.removeChild(currentTooltipElement);
  }
  currentTooltipElement = null;
}

/**
 * 计算并设置提示框位置
 * @param tooltip 提示框元素
 * @param el 绑定指令的元素
 * @param event 鼠标事件对象
 * @param binding 指令绑定对象
 */
function positionTooltip(
  tooltip: HTMLDivElement,
  el: HTMLElement,
  event: MouseEvent,
  binding: CustomTitleDirectiveBinding
): void {
  const rect = el.getBoundingClientRect(); // 绑定元素的位置和尺寸
  const { placement = "bottom", offset = 10 } =
    typeof binding.value === "object" ? binding.value : {};

  let x = 0;
  let y = 0;

  switch (placement) {
    case "top":
      x = rect.left + rect.width / 2 - tooltip.offsetWidth / 2;
      y = rect.top - tooltip.offsetHeight - offset;
      break;
    case "bottom":
      x = rect.left;
      y = rect.bottom + offset;
      break;
    case "left":
      x = rect.left - tooltip.offsetWidth - offset;
      y = rect.top + rect.height / 2 - tooltip.offsetHeight / 2;
      break;
    case "right":
      x = rect.right + offset;
      y = rect.top + rect.height / 2 - tooltip.offsetHeight / 2;
      break;
    default: // 默认为鼠标位置偏移
      x = event.clientX + 15;
      y = event.clientY + 15;
  }

  // 简单的视口边界检查
  if (x + tooltip.offsetWidth > window.innerWidth - 10) {
    x = window.innerWidth - tooltip.offsetWidth - 10;
  }
  if (x < 10) {
    // 防止超出左侧
    x = 10;
  }
  if (y + tooltip.offsetHeight > window.innerHeight - 10) {
    y = window.innerHeight - tooltip.offsetHeight - 10;
  }
  if (y < 10) {
    // 防止超出顶部
    y = 10;
  }

  tooltip.style.left = `${x}px`;
  tooltip.style.top = `${y}px`;
}

export const customTitle: CustomTitleDirective = {
  /**
   * mounted 钩子：当指令绑定到元素且元素已插入到 DOM 时调用。
   */
  //@ts-ignore
  mounted(el: HTMLElement, binding: CustomTitleDirectiveBinding) {
    let showTimer: ReturnType<typeof setTimeout> | null = null;

    const showTooltipHandler = (event: MouseEvent) => {
      // 如果有隐藏定时器，清除它，表示用户又回到了元素上
      if (currentHideTimer) {
        clearTimeout(currentHideTimer);
        currentHideTimer = null;
      }

      // 获取指令值
      const value = binding.value;
      const content = typeof value === "string" ? value : value.content;

      if (!content) return;

      // 清除任何可能存在的旧的显示定时器
      if (showTimer) clearTimeout(showTimer);

      const delay = binding.modifiers.delay ? 300 : 0; // 默认延迟 300ms

      showTimer = setTimeout(() => {
        removeTooltipElement(); // 确保前一个提示被移除
        const tooltip = createTooltipElement(value); // 传入原始值，以便在createTooltipElement中处理html修饰符等
        positionTooltip(tooltip, el, event, binding);
        tooltip.style.opacity = "1";
      }, delay);
    };

    const hideTooltipHandler = () => {
      if (showTimer) {
        clearTimeout(showTimer); // 清除显示定时器
        showTimer = null;
      }
      // 延迟移除提示框，防止鼠标在元素和提示框之间快速移动时闪烁
      currentHideTimer = setTimeout(() => {
        removeTooltipElement();
      }, 100); // 100ms 延迟
    };

    // 将事件监听器附加到元素上，并将函数引用存储在元素上以便在 unmounted 时移除
    //@ts-ignore
    el.__customTitle_mouseenter_handler__ = showTooltipHandler;
    //@ts-ignore
    el.__customTitle_mouseleave_handler__ = hideTooltipHandler;
    //@ts-ignore
    el.addEventListener("mouseenter", el.__customTitle_mouseenter_handler__);
    //@ts-ignore
    el.addEventListener("mouseleave", el.__customTitle_mouseleave_handler__);
  },

  /**
   * updated 钩子：当包含指令的组件 VNode 更新后调用。
   */
  //@ts-ignore
  updated(el: HTMLElement, binding: CustomTitleDirectiveBinding) {
    let showTimer: ReturnType<typeof setTimeout> | null = null;

    const showTooltipHandler = (event: MouseEvent) => {
      // 如果有隐藏定时器，清除它，表示用户又回到了元素上
      if (currentHideTimer) {
        clearTimeout(currentHideTimer);
        currentHideTimer = null;
      }

      // 获取指令值
      const value = binding.value;
      const content = typeof value === "string" ? value : value.content;

      if (!content) return;

      // 清除任何可能存在的旧的显示定时器
      if (showTimer) clearTimeout(showTimer);

      const delay = binding.modifiers.delay ? 300 : 0; // 默认延迟 300ms

      showTimer = setTimeout(() => {
        removeTooltipElement(); // 确保前一个提示被移除
        const tooltip = createTooltipElement(value); // 传入原始值，以便在createTooltipElement中处理html修饰符等
        positionTooltip(tooltip, el, event, binding);
        tooltip.style.opacity = "1";
      }, delay);
    };

    const hideTooltipHandler = () => {
      if (showTimer) {
        clearTimeout(showTimer); // 清除显示定时器
        showTimer = null;
      }
      // 延迟移除提示框，防止鼠标在元素和提示框之间快速移动时闪烁
      currentHideTimer = setTimeout(() => {
        removeTooltipElement();
      }, 100); // 100ms 延迟
    };

    // 将事件监听器附加到元素上，并将函数引用存储在元素上以便在 unmounted 时移除
    //@ts-ignore
    el.__customTitle_mouseenter_handler__ = showTooltipHandler;
    //@ts-ignore
    el.__customTitle_mouseleave_handler__ = hideTooltipHandler;
    //@ts-ignore
    el.addEventListener("mouseenter", el.__customTitle_mouseenter_handler__);
    //@ts-ignore
    el.addEventListener("mouseleave", el.__customTitle_mouseleave_handler__);
  },

  /**
   * beforeUnmount 钩子：当指令绑定到的元素从 DOM 中移除前调用。
   */
  beforeUnmount(el: HTMLElement) {
    // 清理事件监听器和任何活动的定时器
    //@ts-ignore
    if (el.__customTitle_mouseenter_handler__) {
      //@ts-ignore
      el.removeEventListener("mouseenter", el.__customTitle_mouseenter_handler__);
      //@ts-ignore
      delete el.__customTitle_mouseenter_handler__;
    }
    //@ts-ignore
    if (el.__customTitle_mouseleave_handler__) {
      //@ts-ignore
      el.removeEventListener("mouseleave", el.__customTitle_mouseleave_handler__);
      //@ts-ignore
      delete el.__customTitle_mouseleave_handler__;
    }
    removeTooltipElement(); // 确保提示框被移除
  }
};

// 扩展 HTMLElement 接口以存储我们的处理函数引用
//@ts-ignore
declare module "@vue/runtime-core" {
  interface HTMLElement {
    __customTitle_mouseenter_handler__?: (event: MouseEvent) => void;
    __customTitle_mouseleave_handler__?: () => void;
  }
}
